/** Opens the Hub and starts the walkthrough. */
export const INCIDENT_TOUR_HREF = '/hub?incidentTour=1';

export const INCIDENT_TOUR_PARAM = 'incidentTour';

/** Survives the IR → Planning P redirect after the tour escalates this workspace. */
export const INCIDENT_TOUR_CONTINUE_KEY = 'incidentTourContinue';

/**
 * An incident's life spans several routes, and driver.js does not survive
 * navigation, so each route runs its own phase and hands off through the URL.
 */
export type IncidentTourPhase =
  | 'hub'
  | 'wizard'
  | 'workspace'
  | 'planning'
  | 'archive';

export const INCIDENT_TOUR_WIZARD_HREF = `/create/incident?${INCIDENT_TOUR_PARAM}=1`;

/** Catalog type the tour creates. */
export const INCIDENT_TOUR_DEMO_TYPE = 'Exercise';

/** Workspace name written into the create form for this walkthrough. */
export function demoIncidentName(
  orgName: string,
  existingNames: readonly string[] = []
): string {
  const name = orgName.trim() || 'organization';
  const base = `demo incident ${name}`;
  const taken = new Set(
    existingNames.map((existing) =>
      stripExerciseIncidentNamePrefix(existing).trim().toLowerCase()
    )
  );
  if (!taken.has(base.toLowerCase())) {
    return base;
  }
  for (let index = 1; index <= 99; index += 1) {
    const candidate = `${base} ${String(index).padStart(2, '0')}`;
    if (!taken.has(candidate.toLowerCase())) {
      return candidate;
    }
  }
  return `${base} ${Date.now()}`;
}

/** Stored Exercise names get a fixed prefix; uniqueness still applies to the rest. */
function stripExerciseIncidentNamePrefix(name: string): string {
  const prefix = 'Exercise Exercise Exercise ';
  return name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

export function incidentTourWorkspaceHref(incidentId: string): string {
  return `/iap/${incidentId}/initial-response?${INCIDENT_TOUR_PARAM}=1`;
}

/** Planning P on the working period, opened on the first phase. */
export function incidentTourPlanningHref(incidentId: string): string {
  const params = new URLSearchParams({
    mode: 'future',
    formsView: 'working',
    phase: '1',
    [INCIDENT_TOUR_PARAM]: 'planning',
  });
  return `/iap/${incidentId}/dashboard?${params.toString()}`;
}

export function incidentTourArchiveHref(incidentId: string): string {
  return `/iap/${incidentId}/settings?${INCIDENT_TOUR_PARAM}=archive`;
}

/** A host starts its phase for the generic flag or for its own phase name. */
export function wantsIncidentTour(
  param: string | null,
  phase: IncidentTourPhase
): boolean {
  return param === '1' || param === phase;
}

export const INCIDENT_TOUR_PHASE_LABELS: Record<IncidentTourPhase, string> = {
  hub: 'Hub',
  wizard: 'Create',
  workspace: 'Workspace',
  planning: 'Planning P',
  archive: 'Close out',
};

export type IncidentTourStepId =
  | 'intro'
  | 'createMenu'
  | 'createIncident'
  | 'nameLocation'
  | 'buildTeam'
  | 'notifications'
  | 'createSubmit'
  | 'workspace'
  | 'ics201'
  | 'roster'
  | 'tasks'
  | 'reports'
  | 'escalate'
  | 'planningP'
  | 'planningForms'
  | 'incidentObjectives'
  | 'organizationAssignmentList'
  | 'assignmentList'
  | 'incidentRadioCommunicationsPlan'
  | 'medicalPlan'
  | 'iapSafetyAnalysis'
  | 'incidentActionPlan'
  | 'advanceOpPeriod'
  | 'opPeriodSettings'
  | 'archive';

export type IncidentTourStepDef = {
  id: IncidentTourStepId;
  phase: IncidentTourPhase;
  title: string;
  description: string;
  /** `data-tour` value. */
  tour?: string;
  /** Raw selector, for chrome that already carries a stable id. */
  selector?: string;
  /**
   * Planning P form steps: the `widgetRegistry` name to select in the Forms
   * dropdown before this step is shown.
   */
  widget?: string;
};

/**
 * Index of the Operations Briefing in `PLANNING_PHASES`, where Advance
 * Operational Period lives.
 */
export const INCIDENT_TOUR_OPERATIONS_BRIEFING_PHASE = 7;

export const INCIDENT_TOUR_STEPS: IncidentTourStepDef[] = [
  {
    id: 'intro',
    phase: 'hub',
    title: 'The life of an incident',
    description:
      'An incident is the workspace a response lives in: its roster, its tasks, its ICS forms, and its reports. This walkthrough creates a demo exercise named after your organization, then follows it from the Hub through staffing and tasking, into the Planning P cycle and its operational periods, to the day you close it out. It does not archive.',
  },
  {
    id: 'createMenu',
    phase: 'hub',
    selector: '#hub-create-menu',
    title: 'Everything starts here',
    description:
      'The Hub watches your whole organization, and + Create is its single entry point for new work — incidents, notifications, events, and actions. Next opens the menu.',
  },
  {
    id: 'createIncident',
    phase: 'hub',
    tour: 'hub-create-incident',
    title: 'Create Incident',
    description:
      'The other items stay on the Hub. Create Incident opens a full-screen form, because standing up a response also means naming it, staffing it, and telling people they are activated. Next opens that form.',
  },
  {
    id: 'nameLocation',
    phase: 'wizard',
    tour: 'create-incident-name',
    title: 'Name and location',
    description:
      'The only required step. The name and incident type identify the response, the sponsor organization decides who can see it, and the AOR and location put it on the map so reports and event automation can find it later.',
  },
  {
    id: 'buildTeam',
    phase: 'wizard',
    tour: 'create-incident-team',
    title: 'Build the team',
    description:
      'Activating an ICS team pulls in every member at once, and the org chart below seats them. Whatever you set here becomes the incident roster on day one. You can also leave it empty and staff the roster once the response is running.',
  },
  {
    id: 'notifications',
    phase: 'wizard',
    tour: 'create-incident-notifications',
    title: 'Tell them they are activated',
    description:
      'Pick how activated people hear about it — email, text, automated call, or in-app — and write the message they get. Everyone except you receives a request to confirm or decline the activation.',
  },
  {
    id: 'createSubmit',
    phase: 'wizard',
    tour: 'create-incident-submit',
    title: 'Create the incident',
    description:
      'This button creates the incident and drops you straight into its workspace. Next creates a demo exercise named after your organization and opens that workspace so the rest of the walkthrough can follow it.',
  },
  {
    id: 'workspace',
    phase: 'workspace',
    tour: 'incident-ir-stepper',
    title: 'The incident workspace',
    description:
      'Creating an incident lands you here, on initial response. The stepper walks the first hours of a response: overview, objectives and actions, roster, resources, safety, briefing, and reports. The left sidebar holds the same surfaces plus forms, the calendar, and settings.',
  },
  {
    id: 'ics201',
    phase: 'workspace',
    tour: 'incident-ir-forms',
    title: 'Incident Briefing ICS-201',
    description:
      'Initial response runs from one form. The ICS-201 captures the situation and map sketch, the initial objectives, the current organization, and the resources on hand or ordered. The Forms dropdown lets you open any other ICS form early, but the 201 is the record of this phase — it is exported as the period 0 briefing the moment you escalate.',
  },
  {
    id: 'roster',
    phase: 'workspace',
    tour: 'ics201-organization',
    title: 'Staff the roster',
    description:
      'The roster is this incident’s ICS org chart. Seat people into positions here and they can be assigned work, notified, and shown on ICS forms. Anyone you activated during create is already on it.',
  },
  {
    id: 'tasks',
    phase: 'workspace',
    tour: 'ics201-objectives',
    title: 'Set objectives, then task the work',
    description:
      'Objectives say what this operational period is trying to achieve; actions are the tasks that get you there, each with an owner, a due date, and a status. The Actions tracker in the sidebar is the same work seen across the whole incident.',
  },
  {
    id: 'reports',
    phase: 'workspace',
    tour: 'incident-ir-content',
    title: 'Report out',
    description:
      'Reports turn what the roster did into a situation report or an operations order for the next period. Drafting an OPORD here can also generate the actions that carry the plan forward.',
  },
  {
    id: 'escalate',
    phase: 'workspace',
    tour: 'incident-escalate',
    title: 'Escalate the response',
    description:
      'When an incident outgrows initial response, Escalate Response moves it into the Planning P cycle. It marks the incident escalated, exports the ICS-201 as the period 0 briefing, seeds the meeting calendar, and opens Planning P. It does not advance the operational period — the first cycle prepares period 1. Next escalates this workspace and opens its Planning P.',
  },
  {
    id: 'planningP',
    phase: 'planning',
    tour: 'planning-stepper',
    title: 'The Planning P',
    description:
      'Every operational period runs the same eight phases: IC/UC Objectives, Command and General Staff Meeting, Prepare Tactics, Tactics Meeting, Prepare Planning, Planning Meeting, IAP Prep and Approval, and Operations Briefing. The stepper lists each phase’s forms and their status, Complete Phase records progress, and the meetings sit on the incident calendar.',
  },
  {
    id: 'planningForms',
    phase: 'planning',
    tour: 'planning-forms',
    title: 'The ICS forms',
    description:
      'The Forms dropdown lists every ICS form for the working period — the current phase’s forms first, then the rest. Each opens as an editable card below. Next walks the core forms that make up an Incident Action Plan.',
  },
  {
    id: 'incidentObjectives',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'incidentObjectives',
    title: 'Incident Objectives ICS-202',
    description:
      'The first form of the cycle, written in the IC/UC Objectives phase. It states what the period must achieve, the command emphasis, the general situational awareness, and the safety message, and it lists the attachments that make up the IAP. Everything downstream is planned against these objectives.',
  },
  {
    id: 'organizationAssignmentList',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'organizationAssignmentList',
    title: 'Organization Assignment List ICS-203',
    description:
      'Who holds each ICS position for the period, from Incident Command and the Command Staff through the General Staff sections, branches, and divisions. It is drawn from the roster you staffed, so seating people there is what fills this form.',
  },
  {
    id: 'assignmentList',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'assignmentList',
    title: 'Assignment List ICS-204',
    description:
      'One per division or group: the resources assigned, their work assignments, special instructions, and the communications each uses. It is built from the work analysis and the Tactics Meeting, and it is what field supervisors are briefed from.',
  },
  {
    id: 'incidentRadioCommunicationsPlan',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'incidentRadioCommunicationsPlan',
    title: 'Incident Radio Communications Plan ICS-205',
    description:
      'The radio channels and frequencies in use, by function and assignment, so every 204 can point at the right net. The Communications Unit Leader owns it.',
  },
  {
    id: 'medicalPlan',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'medicalPlan',
    title: 'Medical Plan ICS-206',
    description:
      'Medical aid stations, transportation, hospitals, and the emergency procedures for responders — not the public. The Medical Unit Leader prepares it and the Safety Officer reviews it.',
  },
  {
    id: 'iapSafetyAnalysis',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'iapSafetyAnalysis',
    title: 'IAP Safety Analysis ICS-215A',
    description:
      'The Safety Officer’s hazard analysis of each work assignment and the mitigations required, prepared alongside the Operational Planning Worksheet during Prepare Tactics and reviewed at the Tactics Meeting.',
  },
  {
    id: 'incidentActionPlan',
    phase: 'planning',
    tour: 'planning-form-card',
    widget: 'incidentActionPlan',
    title: 'The Incident Action Plan',
    description:
      'The IAP is the package: the 202, 203, 204s, 205, 206, and the other forms this period needs, assembled in IAP Prep and Approval and signed by the Incident Commander. Exporting it here produces the document briefed at the Operations Briefing.',
  },
  {
    id: 'advanceOpPeriod',
    phase: 'planning',
    tour: 'planning-advance-op',
    title: 'Advance the operational period',
    description:
      'Advance Operational Period lives on the last phase, the Operations Briefing. It closes this planning cycle: the approved IAP becomes the plan for the new current period, the working forms roll forward, and the Planning P starts over for the period after. This walkthrough does not press it. Next opens incident settings.',
  },
  {
    id: 'opPeriodSettings',
    phase: 'archive',
    tour: 'incident-op-period',
    title: 'Operational periods',
    description:
      'Operational Period sets the window for the current period and the start and length of the next one; the interval decides how often the cycle repeats. Advance Operational Period beside it does the same thing as the button on the Operations Briefing, for when you need to roll the period from here.',
  },
  {
    id: 'archive',
    phase: 'archive',
    tour: 'incident-archive',
    title: 'Closing the incident',
    description:
      'When the response is over, Archive retires the workspace. Nothing is deleted — the roster, actions, forms, and reports stay for the record and for after-action review, and Pratus asks which workspace to send you to next. That is the full lifecycle.',
  },
];

export function incidentTourStepsForPhase(
  phase: IncidentTourPhase
): IncidentTourStepDef[] {
  return INCIDENT_TOUR_STEPS.filter((step) => step.phase === phase);
}

export function incidentTourStepIds(): IncidentTourStepId[] {
  return INCIDENT_TOUR_STEPS.map((step) => step.id);
}

export function incidentTourStepTitles(): string[] {
  return INCIDENT_TOUR_STEPS.map((step) => step.title);
}
